import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { supabase } from '~/supabase-client'
import { supabaseGetter } from '~/api/supabase-getter'
import { useUpdateUser } from '~/api/supabase-mutations'
import { useCurrentUser } from '~/common/use-current-user'
import { useAuthContext } from '~/common/context/auth/auth-context'
import { cacheManager } from '~/api/cache-manager'
import AccountAvatar from './account-avatar'

const FieldRow = ({ description, children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
      columnGap: 2,
      rowGap: { xs: 0.5, md: 0 },
      alignItems: 'center',
    }}
  >
    <Typography variant="body2" color="text.secondary">{description}</Typography>
    {children}
  </Box>
)

export const AccountProfileCard = () => {
  const [uploading, setUploading] = useState(false)
  const [username, setUsername] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [avatarUrl, setAvatarUrl] = useState()
  const navigate = useNavigate()
  const { currentUser, error } = useCurrentUser()
  const { signOut } = useAuthContext()
  const { enqueueSnackbar } = useSnackbar()
  const { updateUser, loading: updateLoading } = useUpdateUser()

  useEffect(() => {
    if (error) {
      console.warn(error)
    } else if (currentUser) {
      setUsername(currentUser.username || '')
      setAvatarUrl(currentUser.avatar_url)
      setUserEmail(currentUser.email || '')
    }
  }, [currentUser, error])

  const refetchProfile = useCallback(async () => {
    if (!currentUser?.id) return
    const { data, error: fetchError } = await supabaseGetter.users.getById(currentUser.id)
    if (!fetchError && data) {
      setUsername(data.username || '')
      setAvatarUrl(data.avatar_url)
      setUserEmail(data.email || '')
    }
  }, [currentUser?.id])

  const uploadAvatar = async (event) => {
    try {
      setUploading(true)
      if (!event.target.files?.length) throw new Error('You must select an image to upload.')
      const file = event.target.files[0]
      const fileExt = file.name.split('.').pop()
      const fileName = `${Math.random()}.${fileExt}`
      const filePath = `${currentUser.id}/${fileName}`
      const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file)
      if (uploadError) throw uploadError
      await saveProfile(filePath, false)
      enqueueSnackbar('Avatar uploaded successfully', { variant: 'success' })
      window.dispatchEvent(new Event('profile-updated'))
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setUploading(false)
    }
  }

  const removeAvatar = async () => {
    try {
      setUploading(true)
      if (avatarUrl) {
        await supabase.storage.from('avatars').remove([avatarUrl])
      }
      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: '', updated_at: new Date() })
        .eq('id', currentUser.id)
      if (updateError) throw updateError
      setAvatarUrl(null)
      enqueueSnackbar('Avatar removed successfully', { variant: 'success' })
      window.dispatchEvent(new Event('profile-updated'))
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setUploading(false)
    }
  }

  const saveProfile = useCallback(async (newAvatarUrl, navigateAfter = true) => {
    if (!currentUser) return
    const updates = {
      username,
      avatar_url: newAvatarUrl ?? avatarUrl,
      updated_at: new Date(),
    }
    await updateUser(currentUser.id, updates, {
      onSuccess: async () => {
        if (newAvatarUrl) setAvatarUrl(newAvatarUrl)
        cacheManager.invalidateAll()
        window.dispatchEvent(new Event('profile-updated'))
        await refetchProfile()
        enqueueSnackbar('Profile updated successfully', { variant: 'success' })
        if (navigateAfter) navigate('/dashboard')
      },
      onError: (err) => {
        enqueueSnackbar(err.message, { variant: 'error' })
      },
    })
  }, [currentUser, username, avatarUrl, updateUser, enqueueSnackbar, refetchProfile, navigate])

  const handleSubmit = (event) => {
    event.preventDefault()
    saveProfile(null, true)
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Profile" sx={{ pb: 0 }} />
      <CardContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ height: 120 }}>
              <AccountAvatar url={avatarUrl} size={120} onRemove={removeAvatar} />
            </Box>
            <Button
              component="label"
              variant="outlined"
              disabled={uploading}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              {uploading ? 'Uploading ...' : 'Change Avatar'}
              <Box
                component="input"
                type="file"
                accept="image/*"
                onChange={uploadAvatar}
                disabled={uploading}
                sx={{ visibility: 'hidden', position: 'absolute' }}
              />
              {uploading && <LinearProgress />}
            </Button>
          </Box>

          <FieldRow description="Your email address. Contact support to change.">
            <TextField
              id="email"
              label="Email"
              type="text"
              value={userEmail}
              disabled
              size="small"
              fullWidth
            />
          </FieldRow>

          <FieldRow description="Your display name shown to farm members.">
            <TextField
              id="username"
              label="Name"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              size="small"
              fullWidth
            />
          </FieldRow>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={updateLoading}
              sx={{ textTransform: 'none' }}
            >
              {updateLoading ? 'Saving ...' : 'Save Changes'}
            </Button>
          </Box>

          <Divider />

          <Button
            type="button"
            variant="outlined"
            onClick={signOut}
            color="error"
            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          >
            Sign Out
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}
